import { z } from 'zod';
import * as zfd from 'zod-form-data';
import type { Logger } from '@archipro-website/logger/server';
import type { ActionFunction } from '~/types/route-args';
import { UserFeedbackFeature, UserFeedbackRating } from 'generated/graphql';
import { UserErrorResponse } from '~/modules/root/graphql/responses';
import { submitSiteFeedback } from '~/modules/site-feedback/api/submit-site-feedback.server';
import { isSiteFeedbackAudience } from '~/modules/site-feedback/util/is-site-feedback-audience';

/** API path the feedback modal posts to. */
export const SITE_FEEDBACK_API_ROUTE = '/remix-api/site-feedback';

/**
 * Longest comment accepted. Well past anything a person types into the box, and short enough
 * that the field cannot be used to push arbitrary bulk into the table.
 */
const COMMENT_MAX_LENGTH = 5000;

/**
 * The page is named by its URL alone, with no DirectoryItemID.
 *
 * The mutation accepts an item — Business Manager's prompts send one — but this button reaches
 * pages that have no item at all, and an item that failed to resolve would fail the whole write
 * and lose what the staffer typed. A URL names every page the button can reach, and it is what a
 * reviewer clicks anyway.
 */
const SiteFeedbackInputSchema = z.object({
    pageUrl: z.string().min(1).max(2048),
    rating: z.enum([UserFeedbackRating.ThumbUp, UserFeedbackRating.ThumbDown]),
    comment: z.string().max(COMMENT_MAX_LENGTH).optional(),
});

/**
 * Store one piece of sitewide feedback from a staff member.
 *
 * The staff check is repeated here rather than trusted from the client: the button is hidden for
 * everyone else, but hiding a control is not the same as refusing the request behind it, and this
 * route is reachable by anyone who knows the path.
 */
export const action: ActionFunction = async ({ request, context }) => {
    if (!isSiteFeedbackAudience(context.user)) {
        // The button is only drawn for staff, so a refusal here usually means the reader's
        // session changed between the page render and the submit.
        context.logger.warning('Site feedback refused: reader is not staff', {
            siteFeedback: {
                memberId: context.user?.ID,
                userType: context.user?.__typename,
            },
        });
        throw new Response('Feedback is limited to ArchiPro staff', {
            status: 403,
            statusText: 'Forbidden',
        });
    }

    const parsed = zfd
        .formData(SiteFeedbackInputSchema)
        .safeParse(await request.formData());
    if (!parsed.success) {
        throw new UserErrorResponse(parsed.error);
    }

    const { pageUrl, rating, comment } = parsed.data;

    const result = await submitSiteFeedback({
        ...context,
        directoryItemId: null,
        pageUrl,
        feature: UserFeedbackFeature.Revamp2026,
        rating,
        comment: comment?.trim() || null,
    });
    logUnstoredFeedback(context.logger, result, {
        memberId: context.user?.ID,
        pageUrl,
    });

    return result;
};

/**
 * Record a submission that did not become a stored row.
 *
 * The API reports a refusal as a union member inside a 200, and none of its error members are
 * logged by the API itself, so without this a staffer's failed note leaves no trace anywhere
 * but their own browser.
 */
const logUnstoredFeedback = (
    logger: Logger,
    result: Awaited<ReturnType<typeof submitSiteFeedback>>,
    meta: { memberId: number | undefined; pageUrl: string },
) => {
    if (result instanceof Response) {
        logger.error('Site feedback was not stored: request failed', {
            siteFeedback: { ...meta, status: result.status },
        });
        return;
    }

    const payload = result.userFeedbackUpsert;
    if (payload.__typename === 'UserFeedback') return;

    logger.warning('Site feedback was not stored: API refused it', {
        siteFeedback: {
            ...meta,
            errorType: payload.__typename,
            message: payload.Message,
        },
    });
};
